// Owner: shell. Values this browser keeps between visits (local storage) or for the tab (session storage),
// checked as they are read: what is stored may come from another version of the app or have been edited, so
// a value that does not fit its schema reads as absent. Storage that is unavailable (private mode, blocked
// cookies) reads as empty and ignores writes.
import type { z } from 'zod';

export type StorageArea = 'local' | 'session';

function storage(area: StorageArea): Storage | null {
  try {
    return area === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

/** The JSON value stored under `key` when it fits `schema`; null when there is none or it does not fit. */
export function readStored<S extends z.ZodType>(
  key: string,
  schema: S,
  area: StorageArea = 'local',
): z.output<S> | null {
  try {
    const raw = storage(area)?.getItem(key);
    if (raw === null || raw === undefined) return null;
    const parsed = schema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Store `value` as JSON under `key` (nothing happens when storage is unavailable or full). */
export function writeStored(key: string, value: unknown, area: StorageArea = 'local'): void {
  try {
    storage(area)?.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable or full */
  }
}

/** Forget what is stored under `key` (nothing happens when storage is unavailable). */
export function clearStored(key: string, area: StorageArea = 'local'): void {
  try {
    storage(area)?.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}
